import {
  Accordion,
  AccordionSummary,
  Link,
  Typography,
} from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { formatDate } from '@superdispatch/dates';
import { Column, Columns, Inline, Stack } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { useState } from 'react';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { formatCurrency, formatPlural } from 'shared/helpers/IntlHelpers';
import { InstantTransferPriceInformation } from 'shared/modules/superpay/instant-transfer/InstantTransferPriceInformation';
import { SuperPayInstantTransferDialogDTO } from 'shared/modules/superpay/SuperPayDTO';
import { LoadWaypoints } from 'shared/ui/LoadWaypoints';
import styled from 'styled-components';

const StyledAccordion = styled(Accordion)`
  position: static;
  &.MuiPaper-root {
    border: none;
    margin: 0;
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  &.MuiAccordionSummary-root {
    padding: 0;
    min-height: 0;
  }

  & .MuiAccordionSummary-content,
  & .MuiAccordionSummary-content.Mui-expanded {
    margin: 0;
  }
`;

const StyledBox = styled(Box)`
  flex-wrap: wrap;
`;

export function InstantTransferFormDescription({
  dialogInformation,
}: {
  dialogInformation: SuperPayInstantTransferDialogDTO;
}) {
  const [isExpanded, setExpanded] = useState(false);

  const {
    load_guid,
    load_id,
    origin,
    destination,
    vehicles,
    bank_account,
    price,
    expected_date,
  } = dialogInformation;

  const [mainVehicle = ''] = vehicles || [];
  const numberVehicles =
    vehicles?.length && vehicles.length > 1 ? vehicles.length - 1 : 0;

  const formattedPrice = formatCurrency(price, { minimumFractionDigits: 0 });

  return (
    <Stack space="large">
      <Box padding="xsmall">
        <Stack>
          <Box maxWidth={['220px', '100%']}>
            <Typography>
              {formattedPrice} Payment is expected on{' '}
              {formatDate(expected_date, { variant: 'Date', fallback: '' })}
            </Typography>
          </Box>
          <Box color="Yellow500">
            <Stack space="xxsmall">
              <Typography variant="h2" color="textPrimary">
                Skip the wait.
              </Typography>
              <Box maxWidth="400px">
                <Typography variant="h2">
                  Get paid now with Super Dispatch Instant Transfer.
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Box>
      <Box marginTop={['none', 'none', 'small']}>
        <Stack space="small">
          <Stack space="xxsmall">
            <Typography variant="body1">Bank Account</Typography>
            <Typography>{bank_account || 'No data'}</Typography>
          </Stack>
          <Stack space="xxsmall">
            <Typography variant="body1">Load ID</Typography>
            <StyledBox
              display="flex"
              alignItems="baseline"
              justifyContent="space-between"
            >
              {isWebView ? (
                load_id
              ) : (
                <Link
                  underline="none"
                  color="primary"
                  href={`/tms/loads/${load_guid}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {load_id}
                </Link>
              )}
              <StyledAccordion
                expanded={isExpanded}
                onChange={() => {
                  setExpanded(!isExpanded);
                }}
              >
                <StyledAccordionSummary>
                  <Inline verticalAlign="center" noWrap={true}>
                    <Typography color="textSecondary">
                      {isExpanded ? 'Less Details' : 'More Details'}
                    </Typography>
                    <Typography color="textSecondary">
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </Typography>
                  </Inline>
                </StyledAccordionSummary>
              </StyledAccordion>
            </StyledBox>
          </Stack>
          {isExpanded && (
            <Columns collapseBelow="tablet" space="small">
              <Column>
                <Stack space="xxsmall">
                  <Typography variant="body1">Origin & Destination</Typography>
                  <LoadWaypoints
                    space="small"
                    pickupAddress={<Typography>{origin}</Typography>}
                    deliveryAddress={<Typography>{destination}</Typography>}
                  />
                </Stack>
              </Column>
              <Column>
                {mainVehicle && (
                  <Stack space="xxsmall">
                    <Typography variant="body1">Vehicles</Typography>
                    <Typography>{mainVehicle}</Typography>
                    {!!numberVehicles && (
                      <Typography>
                        + {numberVehicles}{' '}
                        {formatPlural(numberVehicles, 'vehicle', 'vehicles')}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Column>
            </Columns>
          )}
        </Stack>
      </Box>
      <InstantTransferPriceInformation
        data={dialogInformation}
        totalTitle="To Be Paid"
      />
    </Stack>
  );
}
